import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import {
  Clock,
  ExternalLink,
  Sparkles,
  Lock,
  ChevronDown,
  ChevronUp,
  Loader2,
  Compass,
  Plus,
  Users,
  Inbox,
  Scale,
  Lightbulb,
  Newspaper,
} from 'lucide-react'
import { api, type FeedVideo, type FeedUsage, type PopularChannel, type DiscoverVideo, type RecommendedChannel, type ChannelPreview, type BriefingCluster } from '@/lib/api'
import { Card } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { SearchTrigger } from '@/components/search'
import { RundownInput } from '@/components/rundown-input'
import { StarButton } from '@/components/star-button'

const GROUP_THRESHOLD = 1

function formatDuration(seconds: number | null): string {
  if (!seconds) return ''
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status || status === 'ready') return null
  // Hide internal statuses that shouldn't be surfaced to users.
  if (status === 'skipped_no_quota') return null
  const variants: Record<string, { variant: 'secondary' | 'warning' | 'destructive'; label: string }> = {
    pending: { variant: 'secondary', label: 'Processing' },
    processing: { variant: 'warning', label: 'Analyzing' },
    failed: { variant: 'destructive', label: 'Failed' },
  }
  const config = variants[status] || { variant: 'secondary' as const, label: status }
  return <Badge variant={config.variant}>{config.label}</Badge>
}

function VideoCard({ video }: { video: FeedVideo }) {
  const navigate = useNavigate()
  const isProcessing = video.summary_status === 'pending' || video.summary_status === 'processing'

  return (
    <Card
      className="group cursor-pointer overflow-hidden transition-all hover:shadow-md hover:border-primary/20"
      onClick={() => navigate(`/videos/${video.id}`)}
    >
      <div className="flex gap-4 p-4">
        <div className="relative shrink-0">
          <img
            src={video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_video_id}/mqdefault.jpg`}
            alt=""
            className="h-24 w-40 rounded-lg object-cover bg-muted"
          />
          {video.duration_seconds && (
            <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1.5 py-0.5 text-xs font-medium text-white">
              {formatDuration(video.duration_seconds)}
            </span>
          )}
          {/* Star sits top-right of thumbnail. Only attached when the video
              isn't locked — locked rows shouldn't be starrable since the
              user can't read the summary anyway. */}
          {!video.locked && (
            <div className="absolute right-1 top-1">
              <StarButton
                videoId={video.id}
                initialFavorited={video.is_favorited ?? false}
                variant="overlay"
              />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-medium leading-snug line-clamp-2 group-hover:text-primary transition-colors">
              {video.title}
            </h3>
            <StatusBadge status={video.summary_status} />
          </div>

          <p className="mt-1 text-sm text-muted-foreground">{video.channel_name}</p>

          {video.locked ? (
            <div className="mt-2 flex items-center gap-2 rounded-lg bg-muted/50 p-2">
              <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
              {/* Two distinct lock paths share this strip:
                  - 'preview' (Browse, non-followed channel): tease + Follow CTA
                  - 'quota'   (over monthly limit): Upgrade CTA
                  Default to the quota copy when lock_reason is missing so old
                  feed responses without the field still render sensibly. */}
              {video.lock_reason === 'preview' ? (
                <p className="text-xs text-muted-foreground">
                  Follow this channel to read the full summary.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Summary limit reached.{' '}
                  <Link
                    to="/upgrade"
                    className="font-medium text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Upgrade or buy credits
                  </Link>
                  .
                </p>
              )}
            </div>
          ) : video.summary_short ? (
            <p className="mt-2 text-sm text-foreground/80 line-clamp-2">{video.summary_short}</p>
          ) : isProcessing ? (
            <div className="mt-2 space-y-1.5">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-4/5" />
            </div>
          ) : null}

          <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(video.published_at), { addSuffix: true })}
            </span>
            {!video.locked && video.source_type && (
              <span className="flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                {video.source_type === 'youtube_video' ? 'Video AI' : 'Transcript'}
              </span>
            )}
            <a
              href={video.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3" />
              Watch
            </a>
          </div>
        </div>
      </div>
    </Card>
  )
}

interface ChannelGroupProps {
  channelName: string
  channelThumbnail: string
  videos: FeedVideo[]
}

function ChannelGroup({ channelName, channelThumbnail, videos }: ChannelGroupProps) {
  const [expanded, setExpanded] = useState(false)
  const newest = videos[0]
  const processingCount = videos.filter(
    (v) => v.summary_status === 'pending' || v.summary_status === 'processing'
  ).length

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-accent/40"
      >
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage src={channelThumbnail} />
          <AvatarFallback>{channelName[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate font-semibold">{channelName}</p>
            <Badge variant="secondary">{videos.length} new</Badge>
            {processingCount > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                {processingCount} processing
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            Newest: {newest.title} ·{' '}
            {formatDistanceToNow(new Date(newest.published_at), { addSuffix: true })}
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="space-y-2 border-t bg-muted/20 p-3">
          {videos.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </div>
      )}
    </Card>
  )
}

type FeedItem =
  | { kind: 'video'; sortDate: number; video: FeedVideo }
  | { kind: 'group'; sortDate: number; channelName: string; channelThumbnail: string; videos: FeedVideo[] }

function buildFeedItems(videos: FeedVideo[]): FeedItem[] {
  const byChannel = new Map<string, FeedVideo[]>()
  for (const v of videos) {
    const key = v.channel_name
    const arr = byChannel.get(key)
    if (arr) arr.push(v)
    else byChannel.set(key, [v])
  }

  const items: FeedItem[] = []
  for (const [channelName, group] of byChannel) {
    if (group.length >= GROUP_THRESHOLD) {
      group.sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime())
      const newest = group[0]
      items.push({
        kind: 'group',
        sortDate: new Date(newest.created_at).getTime(),
        channelName,
        channelThumbnail: newest.channel_thumbnail,
        videos: group,
      })
    } else {
      for (const v of group) {
        items.push({ kind: 'video', sortDate: new Date(v.created_at).getTime(), video: v })
      }
    }
  }

  items.sort((a, b) => b.sortDate - a.sortDate)
  return items
}

function PopularChannelCard({
  channel,
  onFollow,
  onPreview,
  following,
}: {
  channel: PopularChannel
  onFollow: (id: string) => void
  onPreview: (id: string) => void
  following: boolean
}) {
  return (
    <Card className="flex items-center gap-3 p-3">
      {/* Avatar + title area is a button that opens the channel preview drawer.
          Keeping it as the main click target (not the whole Card) leaves the
          Follow button as a clean second action without needing stopPropagation. */}
      <button
        type="button"
        onClick={() => onPreview(channel.id)}
        className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity hover:opacity-80"
      >
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage src={channel.thumbnail_url} />
          <AvatarFallback>{channel.title[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{channel.title}</p>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Users className="h-3 w-3" />
              {channel.follower_count} user{channel.follower_count === 1 ? '' : 's'}
            </span>
            {channel.topics && channel.topics.length > 0 && (
              <span className="truncate">
                {channel.topics.slice(0, 2).join(', ')}
              </span>
            )}
          </div>
        </div>
      </button>
      <Button
        variant={following ? 'secondary' : 'default'}
        size="sm"
        disabled={following}
        onClick={() => onFollow(channel.id)}
        className="shrink-0 gap-1"
      >
        {following ? 'Following' : <><Plus className="h-3 w-3" /> Follow</>}
      </Button>
    </Card>
  )
}

function RecommendedChannelCard({
  channel,
  onFollow,
  onPreview,
  following,
}: {
  channel: RecommendedChannel
  onFollow: (id: string) => void
  onPreview: (id: string) => void
  following: boolean
}) {
  return (
    <Card className="flex items-center gap-3 p-3">
      <button
        type="button"
        onClick={() => onPreview(channel.id)}
        className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity hover:opacity-80"
      >
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage src={channel.thumbnail_url} />
          <AvatarFallback>{channel.title[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{channel.title}</p>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            {/* Reason hint — the personalized "why" that justifies the rec.
                Falls back to a generic peer-count line if the reason channel
                couldn't be computed (degenerate case). */}
            {channel.reason_channel ? (
              <span className="truncate">
                Because you follow <span className="font-medium text-foreground/80">{channel.reason_channel}</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <Users className="h-3 w-3" />
                {channel.overlap_users} similar user{channel.overlap_users === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
      </button>
      <Button
        variant={following ? 'secondary' : 'default'}
        size="sm"
        disabled={following}
        onClick={() => onFollow(channel.id)}
        className="shrink-0 gap-1"
      >
        {following ? 'Following' : <><Plus className="h-3 w-3" /> Follow</>}
      </Button>
    </Card>
  )
}

function DiscoverVideoCard({
  video,
  following,
  onFollow,
}: {
  video: DiscoverVideo
  following: boolean
  onFollow: (channelId: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <VideoCard video={video} />
      {/* Compact follow CTA tucked under the card so the discovery surface stays
          recognizable as videos (not as channel rows) but a single click is
          all it takes to add the channel to the user's feed. */}
      <div className="flex items-center justify-end px-1">
        <Button
          size="sm"
          variant={following ? 'secondary' : 'outline'}
          className="h-7 gap-1 px-2 text-xs"
          disabled={following}
          onClick={() => onFollow(video.channel_id)}
        >
          {following ? (
            <>Following {video.channel_name}</>
          ) : (
            <>
              <Plus className="h-3 w-3" /> Follow {video.channel_name}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

type ViewMode = 'feed' | 'browse'

function FeedSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} className="p-4">
          <div className="flex gap-4">
            <Skeleton className="h-24 w-40 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}

function renderItems(items: FeedItem[]) {
  return (
    <div className="space-y-3">
      {items.map((item, i) =>
        item.kind === 'group' ? (
          <ChannelGroup
            key={`group-${item.channelName}-${i}`}
            channelName={item.channelName}
            channelThumbnail={item.channelThumbnail}
            videos={item.videos}
          />
        ) : (
          <VideoCard key={item.video.id} video={item.video} />
        )
      )}
    </div>
  )
}

/**
 * A single "What's happening" topic cluster: synthesized title + source badge,
 * common ground, where channels differ, unique takeaways, and a collapsible
 * list of the underlying videos (reusing VideoCard). The cluster is a funnel
 * into the individual videos, never a replacement for them.
 */
export function ClusterCard({ cluster }: { cluster: BriefingCluster }) {
  const [expanded, setExpanded] = useState(false)
  const channelCount = new Set(cluster.videos.map((v) => v.channel_name)).size
  // Per-video takeaways worth surfacing above the fold (drop empties).
  const takeaways = cluster.videos
    .filter((v) => v.unique_takeaway)
    .map((v) => ({ channel: v.channel_name, text: v.unique_takeaway as string }))

  return (
    <Card className="overflow-hidden border-primary/15">
      <div className="p-4">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold leading-snug">{cluster.title}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {cluster.videos.length} video{cluster.videos.length === 1 ? '' : 's'} · {channelCount}{' '}
              channel{channelCount === 1 ? '' : 's'}
            </p>
          </div>
        </div>

        {cluster.common_ground && (
          <p className="mt-3 text-sm text-foreground/80">{cluster.common_ground}</p>
        )}

        {cluster.disagreements.length > 0 && (
          <div className="mt-3">
            <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <Scale className="h-3.5 w-3.5" /> Where they differ
            </p>
            <ul className="mt-1.5 space-y-1.5">
              {cluster.disagreements.map((d, i) => (
                <li key={i} className="text-sm text-foreground/80">
                  <span>{d.point}</span>
                  {d.channels.length > 0 && (
                    <span className="text-muted-foreground"> — {d.channels.join(', ')}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {takeaways.length > 0 && (
          <div className="mt-3">
            <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <Lightbulb className="h-3.5 w-3.5" /> Unique takeaways
            </p>
            <ul className="mt-1.5 space-y-1.5">
              {takeaways.map((t, i) => (
                <li key={i} className="text-sm text-foreground/80">
                  <span className="font-medium text-foreground/90">{t.channel}:</span> {t.text}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between border-t px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent/40"
      >
        <span className="font-medium text-muted-foreground">
          {expanded ? 'Hide videos' : `Videos in this topic`}
        </span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="space-y-2 border-t bg-muted/20 p-3">
          {cluster.videos.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </div>
      )}
    </Card>
  )
}

/**
 * "What's happening" section — the topic-clustered briefing rendered above the
 * chronological feed. Renders nothing when there are no clusters (cold start /
 * still generating), so the feed looks exactly as it did before.
 */
function BriefingSection({ clusters }: { clusters: BriefingCluster[] }) {
  if (clusters.length === 0) return null
  return (
    <div className="mb-8">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          <Newspaper className="h-4 w-4" /> Your niche brief
        </h2>
        <Link to="/briefings" className="text-xs font-medium text-primary hover:underline">
          See all →
        </Link>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        What your channels are converging on — and where they disagree.
      </p>
      <div className="space-y-3">
        {clusters.map((c) => (
          <ClusterCard key={c.id} cluster={c} />
        ))}
      </div>
    </div>
  )
}

/**
 * Channel preview drawer. Self-contained: takes a channelId + control props,
 * fetches the preview on mount/channel-change, and renders a header (avatar,
 * title, topic chips, description) plus the last 5 ready summaries reusing
 * VideoCard. Follow CTA at the bottom mirrors the parent's follow state so
 * the user can add the channel without leaving the drawer.
 *
 * Mounted only while a channel is selected (via the {selectedId && ...} check
 * in FeedPage) — this keeps the fetch tied to the open lifecycle and avoids
 * a stale-data window when the drawer reopens for a different channel.
 */
function ChannelPreviewDrawer({
  channelId,
  following,
  onFollow,
}: {
  channelId: string
  following: boolean
  onFollow: (id: string) => void
}) {
  const [data, setData] = useState<{ channel: ChannelPreview; videos: FeedVideo[] } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setData(null)
    api.getChannelPreview(channelId)
      .then((res) => {
        if (!cancelled) setData(res)
      })
      .catch((err) => {
        if (!cancelled) console.error(err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [channelId])

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    )
  }

  if (!data) {
    return (
      <p className="text-sm text-muted-foreground">Couldn't load this channel.</p>
    )
  }

  const { channel, videos } = data

  return (
    <div className="space-y-5">
      <SheetHeader className="text-left">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12 shrink-0">
            <AvatarImage src={channel.thumbnail_url || undefined} />
            <AvatarFallback>{channel.title[0]}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <SheetTitle className="truncate">{channel.title}</SheetTitle>
            {channel.topics && channel.topics.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {channel.topics.slice(0, 4).map((t) => (
                  <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        {channel.description && (
          <p className="line-clamp-3 text-sm text-muted-foreground">
            {channel.description}
          </p>
        )}
      </SheetHeader>

      <Button
        className="w-full gap-1"
        variant={following ? 'secondary' : 'default'}
        disabled={following}
        onClick={() => onFollow(channel.id)}
      >
        {following ? 'Following' : <><Plus className="h-4 w-4" /> Follow channel</>}
      </Button>

      <div>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Recent summaries
        </h3>
        {videos.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent summaries available yet.</p>
        ) : (
          <div className="space-y-2">
            {videos.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export function FeedPage() {
  const [view, setView] = useState<ViewMode>('feed')

  // Feed state
  const [feedVideos, setFeedVideos] = useState<FeedVideo[]>([])
  const [usage, setUsage] = useState<FeedUsage | null>(null)
  const [feedLoading, setFeedLoading] = useState(true)
  const [feedWindow, setFeedWindow] = useState<string>('24')

  // "What's happening" briefing — topic clusters across the user's followed
  // channels for the week. Independent of the window selector (always weekly).
  const [briefingClusters, setBriefingClusters] = useState<BriefingCluster[]>([])

  // Browse state
  const [browseVideos, setBrowseVideos] = useState<FeedVideo[]>([])
  const [browseLoading, setBrowseLoading] = useState(false)
  const [browseWindow, setBrowseWindow] = useState<string>('all')
  const [browseLoaded, setBrowseLoaded] = useState(false)

  // Popular channels state
  const [popularChannels, setPopularChannels] = useState<PopularChannel[]>([])
  const [popularLoading, setPopularLoading] = useState(false)
  const [popularLoaded, setPopularLoaded] = useState(false)
  const [topics, setTopics] = useState<string[]>([])
  const [selectedTopic, setSelectedTopic] = useState<string>('')
  const [justFollowed, setJustFollowed] = useState<Set<string>>(new Set())

  // Discover videos state — content-first discovery: fresh ready-summarized
  // videos from channels the user doesn't follow but whose topics overlap
  // with their existing follows. Loads once when Browse opens; doesn't
  // re-fetch on topic-chip changes (the topic chips drive Popular only).
  const [discoverVideos, setDiscoverVideos] = useState<DiscoverVideo[]>([])
  const [discoverLoading, setDiscoverLoading] = useState(false)
  const [discoverLoaded, setDiscoverLoaded] = useState(false)

  // Recommended channels state — collaborative-filtered "people like you"
  // suggestions. Independent of topic chip selection (it's its own list).
  const [recommendedChannels, setRecommendedChannels] = useState<RecommendedChannel[]>([])
  const [recommendedLoading, setRecommendedLoading] = useState(false)
  const [recommendedLoaded, setRecommendedLoaded] = useState(false)

  // "Popular on Rundown" rail — onboarding-only surface that gives zero-follow
  // users a real preview of summaries on Browse. Lazy-loaded the first time
  // a zero-follow user opens Browse; never re-fetched on the same mount
  // because the backend randomizes the channel pick per request, so any
  // refetch would just shuffle cards for no UX gain.
  const [popularVideos, setPopularVideos] = useState<DiscoverVideo[]>([])
  const [popularVideosLoading, setPopularVideosLoading] = useState(false)
  const [popularVideosLoaded, setPopularVideosLoaded] = useState(false)

  // Channel preview drawer state. Single id at a time; the Sheet's open state
  // is derived from this being non-null. The drawer component is only mounted
  // while `previewChannelId` is set, so its internal fetch effect tracks
  // open/close cleanly without a stale-data window.
  const [previewChannelId, setPreviewChannelId] = useState<string | null>(null)

  // Active follow count — gates the "follow channels to personalize" CTA on
  // Browse. Null while loading so the CTA doesn't flash on mount.
  const [followedCount, setFollowedCount] = useState<number | null>(null)

  const loadFeed = async (w: string, showLoader = true) => {
    if (showLoader) setFeedLoading(true)
    try {
      const { videos, usage } = await api.getFeed(w)
      setFeedVideos(videos)
      setUsage(usage)
    } catch (err) {
      console.error(err)
    } finally {
      setFeedLoading(false)
    }
  }

  const loadBrowse = async (w: string, showLoader = true) => {
    if (showLoader) setBrowseLoading(true)
    try {
      const { videos } = await api.getBrowse(w)
      setBrowseVideos(videos)
      setBrowseLoaded(true)
    } catch (err) {
      console.error(err)
    } finally {
      setBrowseLoading(false)
    }
  }

  // Load feed on mount and when window changes
  useEffect(() => { loadFeed(feedWindow) }, [feedWindow])

  // Load the briefing once on mount. A miss (no run yet) returns [] and kicks
  // background generation server-side; the feed's 60s auto-refresh will pick it
  // up, so we also refetch it there.
  useEffect(() => {
    api.getBriefing().then(({ clusters }) => setBriefingClusters(clusters)).catch(() => {})
  }, [])

  // Load active follow count once on mount for CTA gating
  useEffect(() => {
    api.getChannels({ pageSize: 1, filter: 'followed' })
      .then(({ total }) => setFollowedCount(total))
      .catch(() => setFollowedCount(0))
  }, [])

  // Auto-refresh feed every 60s — also refetch the briefing so a server-side
  // background generation (kicked on the first miss) surfaces without a reload.
  useEffect(() => {
    const interval = setInterval(() => {
      loadFeed(feedWindow, false)
      api.getBriefing().then(({ clusters }) => setBriefingClusters(clusters)).catch(() => {})
    }, 60000)
    return () => clearInterval(interval)
  }, [feedWindow])

  // Refetch when the tab regains focus/visibility. setInterval is throttled (and
  // usually paused) in background tabs, so after switching away and back the feed
  // + briefing can be stale — the "needs a hard refresh" symptom. Refetching on
  // visible surfaces a fresh briefing (incl. a server-side rebuild that completed
  // while the tab was backgrounded) without a manual reload.
  useEffect(() => {
    const refetchIfVisible = () => {
      if (document.visibilityState !== 'visible') return
      loadFeed(feedWindow, false)
      api.getBriefing().then(({ clusters }) => setBriefingClusters(clusters)).catch(() => {})
    }
    document.addEventListener('visibilitychange', refetchIfVisible)
    window.addEventListener('focus', refetchIfVisible)
    return () => {
      document.removeEventListener('visibilitychange', refetchIfVisible)
      window.removeEventListener('focus', refetchIfVisible)
    }
  }, [feedWindow])

  const loadPopular = async (topic?: string) => {
    setPopularLoading(true)
    try {
      const [channelsRes, topicsRes] = await Promise.all([
        api.getPopularChannels({ topic: topic || undefined }),
        topics.length === 0 ? api.getBrowseTopics() : Promise.resolve({ topics }),
      ])
      setPopularChannels(channelsRes.channels)
      if (topicsRes.topics !== topics) setTopics(topicsRes.topics)
      setPopularLoaded(true)
    } catch (err) {
      console.error(err)
    } finally {
      setPopularLoading(false)
    }
  }

  const handleFollowPopular = async (channelId: string) => {
    try {
      await api.followChannels([channelId])
      setJustFollowed(prev => new Set(prev).add(channelId))
      setFollowedCount(prev => (prev ?? 0) + 1)
    } catch (err) {
      console.error(err)
    }
  }

  const loadDiscover = async () => {
    setDiscoverLoading(true)
    try {
      const { videos } = await api.getDiscoverVideos()
      setDiscoverVideos(videos)
      setDiscoverLoaded(true)
    } catch (err) {
      console.error(err)
    } finally {
      setDiscoverLoading(false)
    }
  }

  const loadPopularVideos = async () => {
    setPopularVideosLoading(true)
    try {
      const { videos } = await api.getPopularVideos()
      setPopularVideos(videos)
      setPopularVideosLoaded(true)
    } catch (err) {
      console.error(err)
    } finally {
      setPopularVideosLoading(false)
    }
  }

  const loadRecommended = async () => {
    setRecommendedLoading(true)
    try {
      const { channels } = await api.getRecommendedChannels()
      setRecommendedChannels(channels)
      setRecommendedLoaded(true)
    } catch (err) {
      console.error(err)
    } finally {
      setRecommendedLoading(false)
    }
  }

  // Lazy-load browse only when the user switches to it
  useEffect(() => {
    if (view === 'browse' && !browseLoaded) loadBrowse(browseWindow)
    if (view === 'browse' && !popularLoaded) loadPopular()
    if (view === 'browse' && !discoverLoaded) loadDiscover()
    if (view === 'browse' && !recommendedLoaded) loadRecommended()
    // "Popular on Rundown" rail loads only for zero-follow users since that's
    // the only state where we'd render it. Wait until followedCount has
    // resolved (=== 0) so we don't fire the request for users we'll never
    // show the rail to.
    if (view === 'browse' && followedCount === 0 && !popularVideosLoaded) {
      loadPopularVideos()
    }
  }, [view, followedCount])

  // Reload browse when window changes
  useEffect(() => {
    if (view === 'browse') loadBrowse(browseWindow)
  }, [browseWindow])

  // Reload popular when topic filter changes
  useEffect(() => {
    if (view === 'browse') loadPopular(selectedTopic)
  }, [selectedTopic])

  // The Niche Brief is a "today" view — show it ONLY in the 24h window. In 7d/All
  // the same brief alongside a different (often older/empty) video list is
  // confusing, and the dedicated Briefings page now owns history.
  const briefingVisible = feedWindow === '24'
  const showBriefing = briefingVisible && briefingClusters.length > 0

  // Videos surfaced in a cluster are pulled out of the chronological list so they
  // don't appear twice — but ONLY when the brief is actually shown. In 7d/All the
  // brief is hidden, so those videos must fall through to the chronological list.
  const clusteredIds = showBriefing
    ? new Set(briefingClusters.flatMap((c) => c.videos.map((v) => v.id)))
    : new Set<string>()
  const feedItems = buildFeedItems(feedVideos.filter((v) => !clusteredIds.has(v.id)))
  const browseItems = buildFeedItems(browseVideos)
  const processingCount = feedVideos.filter(
    (v) => v.summary_status === 'pending' || v.summary_status === 'processing'
  ).length

  const activeWindow = view === 'feed' ? feedWindow : browseWindow
  const setActiveWindow = view === 'feed' ? setFeedWindow : setBrowseWindow

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">
            {view === 'feed' ? 'Your Feed' : 'Browse'}
          </h1>
          {usage && view === 'feed' && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-24 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${usage.remaining === 0 ? 'bg-destructive' : usage.remaining < 5 ? 'bg-amber-500' : 'bg-primary'}`}
                    style={{ width: `${Math.min(100, (usage.used / usage.limit) * 100)}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground">
                  {usage.used}/{usage.limit} summaries this cycle
                </span>
              </div>
              {usage.credits_available > 0 && (
                <span
                  className="text-xs text-primary/90"
                  title="Rundown Credits are used automatically once your plan allowance runs out. They carry forward across billing cycles."
                >
                  · {usage.credits_available} credit{usage.credits_available === 1 ? '' : 's'} available
                </span>
              )}
              {usage.missed_this_cycle > 0 && (
                <Link
                  to="/upgrade"
                  className="text-xs text-amber-500/90 hover:text-amber-400 transition-colors"
                  title="Videos from your followed channels we couldn't summarize this cycle because quota ran out. Upgrade or buy credits to catch up."
                >
                  · {usage.missed_this_cycle} missed this cycle
                </Link>
              )}
            </div>
          )}
        </div>
        {view === 'feed' && (
          <Tabs value={activeWindow} onValueChange={setActiveWindow}>
            <TabsList>
              <TabsTrigger value="24">24h</TabsTrigger>
              <TabsTrigger value="7d">7d</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {/* Search bar — sits between the page header and the Feed/Browse view
          toggle. Click opens the search modal; Cmd+K (Ctrl+K) opens it from
          anywhere on the page. */}
      <SearchTrigger className="mb-4" />

      {/* Summarize any YouTube link on demand — queues an ad-hoc summary into
          our DB (doesn't follow the channel or add to the Library). */}
      <RundownInput />
      <div className="mb-4" />

      {/* Primary view toggle — prominent segmented control so Browse is
          obviously a peer to Feed, not a tucked-away option. */}
      <Tabs value={view} onValueChange={(v) => setView(v as ViewMode)} className="mb-6">
        <TabsList className="grid w-full grid-cols-2 h-11">
          <TabsTrigger value="feed" className="gap-2 text-sm">
            <Inbox className="h-4 w-4" />
            Feed
          </TabsTrigger>
          <TabsTrigger value="browse" className="gap-2 text-sm">
            <Compass className="h-4 w-4" />
            Browse
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === 'feed' && (
        <>
          {processingCount > 0 && !feedLoading && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              Summarizing {processingCount} video{processingCount === 1 ? '' : 's'}…
            </div>
          )}

          {feedLoading ? (
            <FeedSkeleton />
          ) : followedCount === 0 ? (
            // Zero-follow onboarding CTA. We deliberately check === 0 (not
            // falsy) so that a still-loading followedCount of null falls
            // through to the "No videos yet" state below rather than briefly
            // flashing the welcome screen for users who do have follows.
            <Card className="p-8 text-center sm:p-12">
              <Sparkles className="mx-auto h-10 w-10 text-primary/60" />
              <h3 className="mt-4 text-xl font-semibold">Welcome to Rundown</h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground leading-relaxed">
                Import your YouTube subscriptions and we'll start summarising the channels you actually watch.
                Or jump to Browse to see what others on Rundown are following.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link to="/onboarding">
                  <Button size="lg" className="gap-2">
                    <Sparkles className="h-4 w-4" />
                    Import my subscriptions
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setView('browse')}
                >
                  See what's popular
                </Button>
              </div>
            </Card>
          ) : (
            <>
              {briefingVisible && <BriefingSection clusters={briefingClusters} />}
              {feedItems.length === 0 ? (
                // Only show the empty state when there's genuinely nothing —
                // not when every video happened to land in a cluster shown above.
                !showBriefing && (
                  <Card className="p-12 text-center">
                    <Sparkles className="mx-auto h-10 w-10 text-muted-foreground/50" />
                    {feedWindow === 'all' ? (
                      // Genuinely nothing across all time — the channels just
                      // haven't been summarised yet (or are brand new).
                      <>
                        <h3 className="mt-4 font-semibold">No summaries yet</h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Your followed channels haven't been summarised yet — new uploads will appear here automatically.
                        </p>
                        <div className="mt-4">
                          <Link to="/channels">
                            <Button variant="outline">Manage channels</Button>
                          </Link>
                        </div>
                      </>
                    ) : (
                      // Has follows, but nothing in THIS window. Don't imply they
                      // have no channels — offer to widen the range instead.
                      <>
                        <h3 className="mt-4 font-semibold">
                          Nothing new in the last {feedWindow === '24' ? '24 hours' : '7 days'}
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Your followed channels haven't posted in this window. Try a wider range.
                        </p>
                        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                          {feedWindow === '24' && (
                            <Button variant="outline" onClick={() => setFeedWindow('7d')}>View last 7 days</Button>
                          )}
                          <Button variant="outline" onClick={() => setFeedWindow('all')}>View all</Button>
                        </div>
                      </>
                    )}
                  </Card>
                )
              ) : (
                <>
                  {showBriefing && (
                    <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                      Everything else
                    </h2>
                  )}
                  {renderItems(feedItems)}
                </>
              )}
            </>
          )}
        </>
      )}

      {view === 'browse' && (
        <>
          {/* Zero-follow CTA — three of four Browse sections (Previously
              followed, Fresh in topics, Recommended) are only meaningful once
              the user has at least one active follow. Surface that up-front
              instead of leaving the page feeling sparse. */}
          {followedCount === 0 && (
            <Card className="mb-8 flex flex-col items-center gap-4 p-6 sm:flex-row sm:text-left">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="font-semibold">Personalize your Browse</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Follow a few channels and we'll surface fresh videos in your topics and recommend creators that similar users love.
                </p>
              </div>
              <Link to="/channels" className="shrink-0">
                <Button>Follow channels</Button>
              </Link>
            </Card>
          )}

          {/* Popular on Rundown — onboarding rail. Surfaces ~5 recent ready
              summaries from a randomly-sampled top-N most-followed channel
              pool so a zero-follow user has actual videos to scan and click
              into. Hidden once followedCount > 0; from that point the user's
              personalized rails (Discover / Recommended / Previously
              followed) carry the discovery weight and this rail would feel
              generic next to them. */}
          {followedCount === 0 && popularVideosLoading && (
            <div className="mb-8">
              <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Popular on Rundown
              </h2>
              <FeedSkeleton />
            </div>
          )}
          {followedCount === 0 && !popularVideosLoading && popularVideos.length > 0 && (
            <div className="mb-8">
              <h2 className="mb-1 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Popular on Rundown
              </h2>
              <p className="mb-3 text-xs text-muted-foreground">
                A taste of what others are watching. Follow a channel to start receiving its summaries.
              </p>
              <div className="space-y-3">
                {popularVideos.map((v) => (
                  <DiscoverVideoCard
                    key={v.id}
                    video={v}
                    following={justFollowed.has(v.channel_id)}
                    onFollow={handleFollowPopular}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Previously followed section */}
          {browseLoading ? (
            <FeedSkeleton />
          ) : browseItems.length > 0 && (
            <div className="mb-8">
              <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Previously followed
              </h2>
              {renderItems(browseItems)}
            </div>
          )}

          {/* Discover section: fresh videos in the user's topics, from channels
              they don't follow yet. Hidden entirely if empty (e.g. brand-new
              user with no follows yet) so the page doesn't show a sad state. */}
          {discoverLoading ? (
            <div className="mb-8">
              <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Fresh in topics you follow
              </h2>
              <FeedSkeleton />
            </div>
          ) : discoverVideos.length > 0 && (
            <div className="mb-8">
              <h2 className="mb-1 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Fresh in topics you follow
              </h2>
              <p className="mb-3 text-xs text-muted-foreground">
                Recent videos from channels you might want to add.
              </p>
              <div className="space-y-3">
                {discoverVideos.map((v) => (
                  <DiscoverVideoCard
                    key={v.id}
                    video={v}
                    following={justFollowed.has(v.channel_id)}
                    onFollow={handleFollowPopular}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Recommended for you — collaborative-filtered channels with a
              "Because you follow X" hint. Hidden if the user has no peers
              with overlapping follows (typical for very early-stage user
              base or someone who only follows highly niche channels). */}
          {recommendedLoading ? (
            <div className="mb-8">
              <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Recommended for you
              </h2>
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full rounded-xl" />
                ))}
              </div>
            </div>
          ) : recommendedChannels.length > 0 && (
            <div className="mb-8">
              <h2 className="mb-1 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Recommended for you
              </h2>
              <p className="mb-3 text-xs text-muted-foreground">
                Channels that people with similar tastes follow.
              </p>
              <div className="space-y-2">
                {recommendedChannels.map((ch) => (
                  <RecommendedChannelCard
                    key={ch.id}
                    channel={ch}
                    following={justFollowed.has(ch.id)}
                    onFollow={handleFollowPopular}
                    onPreview={setPreviewChannelId}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Popular channels section */}
          <div>
            <h2 className="mb-1 text-sm font-medium uppercase tracking-wide text-muted-foreground">
              Popular with Rundown users
            </h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Channels followed by other users that you haven't added yet.
            </p>

            {topics.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setSelectedTopic('')}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    selectedTopic === '' ? 'border-primary bg-primary text-primary-foreground' : 'border-input text-muted-foreground hover:text-foreground'
                  }`}
                >
                  All
                </button>
                {topics.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setSelectedTopic(t)}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                      selectedTopic === t ? 'border-primary bg-primary text-primary-foreground' : 'border-input text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            {popularLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full rounded-xl" />
                ))}
              </div>
            ) : popularChannels.length === 0 ? (
              <Card className="p-8 text-center">
                <Compass className="mx-auto h-8 w-8 text-muted-foreground/50" />
                <p className="mt-3 text-sm text-muted-foreground">
                  {selectedTopic
                    ? `No channels found for "${selectedTopic}". Try another topic.`
                    : 'No channels to discover yet. As more users join, popular channels will appear here.'}
                </p>
              </Card>
            ) : (
              <div className="space-y-2">
                {popularChannels.map((ch) => (
                  <PopularChannelCard
                    key={ch.id}
                    channel={ch}
                    following={justFollowed.has(ch.id)}
                    onFollow={handleFollowPopular}
                    onPreview={setPreviewChannelId}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Channel preview drawer. Open state is derived from previewChannelId
          being non-null so closing the sheet (X button, overlay click, esc)
          all funnel through onOpenChange→null. The inner content is mounted
          conditionally so the fetch runs once per opening, not on every
          parent re-render. */}
      <Sheet
        open={previewChannelId !== null}
        onOpenChange={(open) => { if (!open) setPreviewChannelId(null) }}
      >
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
          {previewChannelId && (
            <ChannelPreviewDrawer
              channelId={previewChannelId}
              following={justFollowed.has(previewChannelId)}
              onFollow={handleFollowPopular}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
